import React, { Dispatch, ReactNode, SetStateAction } from 'react';
import { Box, Card, CardActionArea, CardContent, Tooltip, Typography } from '@mui/material';
import { TooltipProps as MaterialTooltipProps } from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import { classNames } from '../../../utils/classNames';
import { pickSoundCardGroupStyles } from '../PickSoundCardGroup/style';
import { CalculationDifficultyOptions } from '../../../commands/practitioner/enums/CalculationDifficultyOptions';
import { getResponsiveSize } from '../../../utils';
import { StroopConfigurationOptions } from '../../../commands/practitioner/enums/StroopConfigurationOptions';
import { Theme } from '../../PhaserApp/enums/Themes';

export interface pickInteractiveCardOption {
  Icon: ReactNode;
  label: string;
  cardTooltip?: string;
  tooltipPosition?: MaterialTooltipProps['placement'];
}

interface PickedCardGroupProps {
  options: pickInteractiveCardOption[];
  name?: string;
  className?: string;
  title: string;
  visual: boolean;
  auditory: boolean;
  calculation: number;
  setVisual: Dispatch<SetStateAction<boolean>>;
  setAuditory: Dispatch<SetStateAction<boolean>>;
  setCalculation: Dispatch<SetStateAction<number>>;
  lastCalculationDifficulty: number | undefined;
  setInteractive: Dispatch<SetStateAction<boolean>>;
  interactive: boolean;
  setStroop: Dispatch<SetStateAction<number>>;
  stroop: number;
  setAnimation: Dispatch<SetStateAction<boolean>>;
  animation: boolean;
  setTheme: Dispatch<SetStateAction<number>>;
  theme: number;
}

export const PickInteractiveCardGroup = (props: PickedCardGroupProps) => {
  const {
    className,
    options,
    name,
    title,
    setVisual,
    visual,
    setAuditory,
    auditory,
    setCalculation,
    calculation,
    lastCalculationDifficulty,
    setInteractive,
    interactive,
    setStroop,
    stroop,
    setAnimation,
    animation,
  } = props;
  const { t } = useTranslation();
  const { pickSoundCardGroup } = pickSoundCardGroupStyles;
  const toId = (label: string) => label.toLowerCase().replace(/[^a-z0-9]+/g, '-');

  const updateInteractiveState = (
    newVisual: boolean,
    newAuditory: boolean,
    newCalculation: number,
    newStroop: number,
    newAnimation: boolean
  ) => {
    if (
      !newVisual &&
      !newAuditory &&
      newCalculation === 0 &&
      newStroop === StroopConfigurationOptions.None &&
      !newAnimation
    ) {
      setInteractive(false);
    } else {
      if (!interactive) {
        setInteractive(true);
      }
    }
  };

  const onChange = (value: string) => {
    let newVisual = visual;
    let newAuditory = auditory;
    let newCalculation = calculation;
    let newStroop = stroop;
    let newAnimation = animation;

    switch (value) {
      case 'visual':
        newVisual = !visual;
        setVisual(newVisual);
        break;
      case 'auditory':
        newAuditory = !auditory;
        setAuditory(newAuditory);
        break;
      case 'calculation':
        newCalculation =
          calculation === 0
            ? !!lastCalculationDifficulty
              ? lastCalculationDifficulty
              : CalculationDifficultyOptions.Easy
            : CalculationDifficultyOptions.Off;
        setCalculation(newCalculation);
        setStroop(StroopConfigurationOptions.None);
        break;
      case 'stroop':
        newStroop =
          stroop === StroopConfigurationOptions.None
            ? StroopConfigurationOptions.Standart
            : StroopConfigurationOptions.None;
        setStroop(newStroop);
        setCalculation(CalculationDifficultyOptions.Off);
        break;
      case 'animation':
        newAnimation = !animation;
        setAnimation(newAnimation);
        break;
      default:
        console.log('Invalid value');
    }

    updateInteractiveState(newVisual, newAuditory, newCalculation, newStroop, newAnimation);
  };

  const isActive = (label: string) => {
    switch (label) {
      case 'visual':
        return visual;
      case 'auditory':
        return auditory;
      case 'calculation':
        return calculation !== CalculationDifficultyOptions.Off;
      case 'stroop':
        return !!stroop;
      case 'animation':
        return animation;
      default:
        return false;
    }
  };

  return (
    <Box sx={pickSoundCardGroup} className={classNames(className || '')}>
      <Tooltip
        placement="left-start"
        disableInteractive
        title={
          <Typography
            style={{ fontSize: getResponsiveSize(11) }}
            color="inherit"
            dangerouslySetInnerHTML={{ __html: t('reaction-on-tooltip') }}
          />
        }
      >
        <Box className="title-container">
          <Typography className="title">{title}</Typography>
        </Box>
      </Tooltip>
      <Box className="items-container items-container-tasks">
        {options?.map((option, index) => (
          <Tooltip
            placement={option.tooltipPosition}
            disableInteractive
            key={`${option?.label}-${title}`}
            title={
              <Typography
                style={{ fontSize: getResponsiveSize(11) }}
                color="inherit"
                dangerouslySetInnerHTML={{ __html: !!option.cardTooltip ? t(`${option.cardTooltip}`) : '' }}
              />
            }
          >
            <Card
              className={classNames(option?.label, { active: isActive(option?.label) }, ['card'])}
              onClick={() => onChange(option?.label)}
              data-testid={`pwid-interactive-${toId(option?.label)}`}
            >
              <CardActionArea>
                <Box className="icon"> {option.Icon}</Box>
                <CardContent>
                  <Typography className="desc" variant="h5">
                    {t(`${option?.label}`)}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
};
